import IconCrossSmall from "@ui/central-icons/icons/IconCrossSmall";
import IconLoadingCircle from "@ui/central-icons/icons/IconLoadingCircle";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/components/core/Tooltip";
import type { FileAttachment } from "./types";

export function FileThumbnails({
  files,
  onRemoveFile,
  isLoading,
}: {
  files: FileAttachment[];
  onRemoveFile?: (index: number) => void;
  isLoading?: boolean;
}) {
  return (
    <div className='tw-flex tw-items-center tw-gap-2 tw-overflow-x-auto tw-px-2 tw-pt-2'>
      {files.map((file, index) => (
        <div
          key={`${file.name}-${index}`}
          className='tw-group tw-relative tw-h-[74px] tw-aspect-[1.72] tw-shrink-0 tw-overflow-hidden tw-rounded-xl'
        >
          {file.isVideo ? (
            <video src={file.previewUrl} className='tw-h-full tw-w-full tw-object-cover' muted playsInline />
          ) : (
            // biome-ignore lint/performance/noImgElement: URL from URL.createObjectURL — next/image requires known dimensions
            <img src={file.previewUrl} alt={file.name} className='tw-h-full tw-w-full tw-object-cover' />
          )}

          {/* Bottom gradient + label — hides on hover (desktop), always visible on mobile */}
          <div className='tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-flex tw-items-end tw-justify-center tw-bg-gradient-to-t tw-from-black/40 tw-to-transparent tw-px-1.5 tw-pb-2 tw-pt-4 tw-transition tw-duration-200 [@media(hover:hover)]:group-hover:tw-opacity-0'>
            <span className='tw-font-display tw-text-[11px] tw-font-bold tw-leading-tight tw-text-white'>
              {file.isVideo ? "@Video" : "@Image"}
            </span>
          </div>

          {/* Upload overlay */}
          {isLoading && (
            <div className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-bg-black/30 tw-backdrop-blur-[2px]'>
              <IconLoadingCircle className={`${iconStrokeFix} tw-size-5 tw-animate-spin tw-text-white`} />
            </div>
          )}

          {/* Gradient scrim behind close button for contrast */}
          {!isLoading && onRemoveFile && (
            <div className='tw-pointer-events-none tw-absolute tw-inset-0 tw-rounded-xl tw-bg-gradient-to-b tw-from-black/25 tw-to-transparent tw-to-40% tw-transition tw-duration-200 [@media(hover:hover)]:tw-opacity-0 [@media(hover:hover)]:group-hover:tw-opacity-100' />
          )}

          {/* Remove button — always visible on touch, hover-only on pointer devices */}
          {!isLoading && onRemoveFile && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type='button'
                    onClick={() => onRemoveFile(index)}
                    aria-label={`Remove ${file.name}`}
                    className='tw-absolute tw-right-1 tw-top-1 tw-flex tw-size-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/80 tw-text-black tw-shadow-sm tw-backdrop-blur tw-transition tw-duration-200 tw-outline-none [@media(hover:hover)]:tw-opacity-0 [@media(hover:hover)]:group-hover:tw-opacity-100 focus-visible:tw-opacity-100 focus-visible:tw-outline-focus'
                  >
                    <IconCrossSmall className={`${iconStrokeFix} tw-size-3.5`} />
                  </button>
                </TooltipTrigger>
                <TooltipContent variant='dark-new'>{`Remove ${file.name}`}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ))}
    </div>
  );
}
